import { useState } from "react";
import { toast } from "sonner";
import { Truck, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import api, { formatApiErrorDetail } from "@/lib/api";

const F = ({ label, value, onChange, testid, placeholder }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{label}</Label>
    <Input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="text-sm" />
  </div>
);

export default function ShippoSettings({ sub, onChange }) {
  const s = sub.shippo || {};
  const [token, setToken] = useState("");
  const [from, setFrom] = useState({ name: "", street1: "", city: "", state: "", zip: "", country: "US", phone: "", ...(s.ship_from || {}) });
  const p = s.default_parcel || {};
  const [parcel, setParcel] = useState({ length: p.length || "10", width: p.width || "8", height: p.height || "4", distance_unit: p.distance_unit || "in", weight: p.weight || "1", mass_unit: p.mass_unit || "lb" });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ship_from: from, default_parcel: parcel };
      if (token.trim()) payload.token = token.trim();
      const { data } = await api.put(`/subaccounts/${sub.id}/shippo`, payload);
      onChange?.(data);
      setToken("");
      toast.success("Shippo settings saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-5 border-border/70 space-y-4" data-testid="shippo-settings">
      <div className="flex items-center gap-2">
        <Truck className="h-4 w-4 text-primary" />
        <h3 className="font-heading font-semibold">Shippo — Shipping Labels</h3>
        {s.configured && (
          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-mono text-emerald-400">
            <CheckCircle2 className="h-3 w-3" /> {String(s.mode || "").toUpperCase()} CONNECTED
          </span>
        )}
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Shippo API Token</Label>
        <Input data-testid="shippo-token-input" type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder={s.configured ? "•••••••• (leave blank to keep current)" : "shippo_test_… or shippo_live_…"} className="font-mono text-sm" />
      </div>

      <div>
        <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">Ship From address</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <F label="Name" value={from.name} onChange={(v) => setFrom({ ...from, name: v })} testid="shippo-from-name" placeholder="Store name" />
          <F label="Phone" value={from.phone} onChange={(v) => setFrom({ ...from, phone: v })} testid="shippo-from-phone" placeholder="+1…" />
          <F label="Street" value={from.street1} onChange={(v) => setFrom({ ...from, street1: v })} testid="shippo-from-street" placeholder="215 Clayton St" />
          <F label="City" value={from.city} onChange={(v) => setFrom({ ...from, city: v })} testid="shippo-from-city" placeholder="San Francisco" />
          <F label="State" value={from.state} onChange={(v) => setFrom({ ...from, state: v })} testid="shippo-from-state" placeholder="CA" />
          <F label="ZIP" value={from.zip} onChange={(v) => setFrom({ ...from, zip: v })} testid="shippo-from-zip" placeholder="94117" />
          <F label="Country" value={from.country} onChange={(v) => setFrom({ ...from, country: v })} testid="shippo-from-country" placeholder="US" />
        </div>
      </div>

      <div>
        <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">Default parcel ({parcel.distance_unit} / {parcel.mass_unit})</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <F label="Length" value={parcel.length} onChange={(v) => setParcel({ ...parcel, length: v })} testid="shippo-parcel-length" />
          <F label="Width" value={parcel.width} onChange={(v) => setParcel({ ...parcel, width: v })} testid="shippo-parcel-width" />
          <F label="Height" value={parcel.height} onChange={(v) => setParcel({ ...parcel, height: v })} testid="shippo-parcel-height" />
          <F label="Weight" value={parcel.weight} onChange={(v) => setParcel({ ...parcel, weight: v })} testid="shippo-parcel-weight" />
        </div>
      </div>

      <Button data-testid="shippo-save-btn" onClick={save} disabled={busy} className="gap-2">
        {busy ? "Saving…" : "Save Shippo settings"}
      </Button>
    </Card>
  );
}
